import { useRouter } from 'next/router';
import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Select, SelectOption, TALENT_DROPDOWN_VALUES } from 'ui-components';
import { ContentSearchFilterSearch } from '~/components/ContentSearchFilter/components/ContentSearchFilterSearch';
import { ContentSearchFilterSort } from '~/components/ContentSearchFilter/components/ContentSearchFilterSort';
import { ContentSearchFilterView } from '~/components/ContentSearchFilter/components/ContentSearchFilterView';
import { ContentSearchPostedAtTimeframe } from '~/components/ContentSearchPostedAtTimeframe';
import { FEATURE_FLAGS, VIEW_OPTIONS } from '~/constants';
import { LocalStorageService } from '~/core/utils/storage';
import {
  CONTENT_SEARCH_PARAMS,
  CONTENT_SEARCH_ROSTER,
  IContentSearchQuery,
} from '~/hooks/useContentSearch';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { ClearAllFiltersButton } from '~/screens/MyAgency/components/ClearAllFiltersButton';
import { selectTalentManager } from '~/selectors/talent-manager';

import { useContentSearchFilterContext } from '../../../ContentSearchFilterContext';
import { getQueryParams } from '../../../SearchContent.util';
import { ContentFiltersV2Toggle } from '../../ContentSearchFilter/ContentFiltersV2Toggle';
import { ContentFiltersV2Drawer } from '../ContentFiltersV2Drawer';

export function ContentSearchSubnavbar() {
  const { onChange, filters, size, clearFilters } =
    useContentSearchFilterContext();
  const router = useRouter();
  // console.log(router);
  const { view } = getQueryParams(router.query);
  const isGlobalSearchPageActive = useFeature(FEATURE_FLAGS.foamGlobalSearch);

  const searchComponent = !isGlobalSearchPageActive && (
    <div className="w-full tab:w-auto">
      <ContentSearchFilterSearch onChange={onChange} filters={filters} />
    </div>
  );

  const sortComponent = (
    <ContentSearchFilterSort isNewUi onChange={onChange} filters={filters} />
  );

  const viewComponent = (
    <ContentSearchFilterView
      onChange={onChange}
      filters={{ ...filters, view: view || VIEW_OPTIONS.grid }}
    />
  );

  const talentDropdownComponent = (
    <InlineContentSearchFilterMyTalent onChange={onChange} />
  );

  const timeframeComponent = (
    <InlineContentSearchFilterPostedAtTimeframe
      onChange={onChange}
      filters={filters}
    />
  );

  const filtersToggleComponent = <ContentFiltersV2Toggle />;

  const clearFiltersButton = size > 0 && clearFilters && (
    <ClearAllFiltersButton title="Clear filters" onClear={clearFilters} />
  );

  return (
    <>
      {/* Desktop View */}
      <div
        data-testid="content-search-subnavbar-desktop"
        className="hidden w-full items-center justify-between space-x-2 tab:flex"
      >
        <div className="flex items-center gap-2">
          {talentDropdownComponent}
          {filtersToggleComponent}
          {timeframeComponent}
          {sortComponent}
          {searchComponent}
          {clearFiltersButton}
        </div>
        <div>{viewComponent}</div>
      </div>

      {/* Mobile View */}
      <div className="flex w-full flex-col space-y-3 pb-4 tab:hidden">
        {/* First Row */}
        <div className="flex w-full items-center gap-2">
          {talentDropdownComponent}
          {filtersToggleComponent}
        </div>

        {/* Second Row */}
        <div className="flex items-center justify-between gap-2">
          {sortComponent}
          {timeframeComponent}
        </div>

        {/* Third Row */}
        {searchComponent && <div className="w-full">{searchComponent}</div>}

        {/* Fourth Row */}
        <div className="flex w-full flex-col justify-center [&>div>div]:w-full [&>div]:w-full [&_label]:flex [&_label]:justify-center">
          {viewComponent}
        </div>

        {/* Fifth Row */}
        {clearFiltersButton && (
          <div className="flex justify-start">{clearFiltersButton}</div>
        )}
      </div>

      <ContentFiltersV2Drawer />
    </>
  );
}

/**
 * @deprecated inline component which will be soon reimplemented with central shadcn based molecule
 */
const InlineContentSearchFilterMyTalent = memo(
  ({
    onChange, // filters,
  }: {
    onChange: (filters: Partial<IContentSearchQuery>) => void;
  }) => {
    const manager = useSelector(selectTalentManager);
    const isAgencyEnabled = manager?.agency?.enabled;
    const router = useRouter();
    const { roster } = router.query;

    // Set default to 'All Talent' if roster is not set or invalid
    const talentOption = useMemo(() => {
      if (
        !roster ||
        ![
          CONTENT_SEARCH_ROSTER.agency,
          CONTENT_SEARCH_ROSTER.myTalent,
        ].includes(roster as CONTENT_SEARCH_ROSTER)
      ) {
        // Update URL with default value
        const newQuery = { ...router.query };
        newQuery.roster = CONTENT_SEARCH_ROSTER.agency;
        router.replace(
          {
            query: newQuery,
          },
          undefined,
          { shallow: true },
        );
        // Set default value in localStorage
        LocalStorageService.setItem(
          'talentOption',
          CONTENT_SEARCH_ROSTER.agency,
        );
        return TALENT_DROPDOWN_VALUES.agency;
      }
      return roster === CONTENT_SEARCH_ROSTER.myTalent
        ? TALENT_DROPDOWN_VALUES.self
        : TALENT_DROPDOWN_VALUES.agency;
    }, [roster, router]);

    const handleChange = (value: string) => {
      const newRoster =
        value === TALENT_DROPDOWN_VALUES.agency
          ? CONTENT_SEARCH_ROSTER.agency
          : CONTENT_SEARCH_ROSTER.myTalent;

      // Update localStorage
      LocalStorageService.setItem('talentOption', newRoster);

      onChange({
        [CONTENT_SEARCH_PARAMS.roster]: newRoster,
        [CONTENT_SEARCH_PARAMS.creator]: '',
      });
    };

    return (
      <div className="inline-flex">
        {isAgencyEnabled && (
          <Select
            value={talentOption}
            onChange={({ value }) => handleChange(value)}
            placeholder="Talent Option"
            trackId="content-search-filter-my-talent"
            // temporary typecasting, to be fixed in followup PR when ui-components is updated
            variant={'gray' as any}
          >
            <SelectOption
              value={TALENT_DROPDOWN_VALUES.agency}
              trackId="talent-option-agency"
            >
              All Talent
            </SelectOption>
            <SelectOption
              value={TALENT_DROPDOWN_VALUES.self}
              trackId="talent-option-roster"
            >
              My Talent
            </SelectOption>
          </Select>
        )}
      </div>
    );
  },
);

/**
 * @deprecated inline component which will be soon reimplemented with central shadcn based molecule
 */
const InlineContentSearchFilterPostedAtTimeframe = memo(
  ({
    onChange,
    filters,
  }: {
    onChange: (filters: Partial<IContentSearchQuery>) => void;
    filters: IContentSearchQuery;
  }) => {
    const { postedAfter = null, postedBefore = null } = filters;

    const onFilter = useCallback(
      (postedAfter?: string, postedBefore?: string) => {
        onChange({
          [CONTENT_SEARCH_PARAMS.postedAfter]: postedAfter,
          [CONTENT_SEARCH_PARAMS.postedBefore]: postedBefore,
        });
      },
      [onChange],
    );

    return (
      <ContentSearchPostedAtTimeframe
        postedAfter={postedAfter}
        postedBefore={postedBefore}
        onFilter={onFilter}
        trackId="content-search-filter-posted-at-timeframe"
      />
    );
  },
);
